import {useEffect, useRef, useState} from 'react';
import {app, onair, print} from "../../modules/config.ts";
import type {DaoMedia} from "../../daos/dao-media.ts";
import _ from "lodash";
import {Utils} from "../../modules/utils.ts";
import {OnairEvent} from "../../modules/onair.ts";
import {wps} from "../../modules/iws.service.ts";

type SliderOptions = {
  minValue?: number;
  maxValue?: number;
  initialValue?: number;
};

function Player() {
  const [media, setMedia] = useState<DaoMedia | null>(null);
  const [mediaPosition, setMediaPosition] = useState<number>(0);
  const [volume, setVolume] = useState<number>(onair.getVolume());
  const [mute, setMute] = useState<boolean>(onair.isMute());
  const [showVolume, setShowVolume] = useState<boolean>(false);

  const volumeLayerRef = useRef<HTMLDivElement | null>(null);
  const volumeWrapRef = useRef<HTMLDivElement | null>(null);

  const trackRef = useRef<HTMLDivElement | null>(null);
  const songRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onair.on(OnairEvent.SCHEDULE, onOnairScheduleHandler);
    onair.on(OnairEvent.MEDIA, onOnairMediaHandler);
    onair.on(OnairEvent.MEDIA_START, onOnairMediaStartHandler);
    onair.on(OnairEvent.MEDIA_END, onOnairMediaEndHandler);
    onair.on(OnairEvent.MEDIA_ERROR, onOnairMediaErrorHandler);
    onair.on(OnairEvent.DURATION, onOnairDurationHandler);

    return () => {
      onair.off(OnairEvent.SCHEDULE, onOnairScheduleHandler);
      onair.off(OnairEvent.MEDIA, onOnairMediaHandler);
      onair.off(OnairEvent.MEDIA_START, onOnairMediaStartHandler);
      onair.off(OnairEvent.MEDIA_END, onOnairMediaEndHandler);
      onair.off(OnairEvent.MEDIA_ERROR, onOnairMediaErrorHandler);
      onair.off(OnairEvent.DURATION, onOnairDurationHandler);
    }
  }, []);

  useEffect(() => {
    setMediaPosition(0);

    if (songRef.current && trackRef.current) {
      songRef.current.style.maxWidth = String(`${trackRef.current.clientWidth - 100}px`);
    }
  }, [media]);

  useEffect(() => {
    const sliderCleanFn = createSlider(
      "slider-container-1",
      "slider-fill-1",
      "slider-thumb-1",
      "slider-value-1",
      setVolume,
      {
        minValue: 0,
        maxValue: 100,
        initialValue: volume,
      }
    );

    if (showVolume) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('touchstart', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);

      sliderCleanFn();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVolume]);

  const needResume = async () => {
    await wps.needResume();
  };

  const refresh = async () => {
    window.location.href = window.location.pathname + "?t=" + new Date().getTime();
    onair.recover();
  };

  /**
   * 모바일 디바이스인경우 뮤트 토글, 볼륨 토글
   */
  function volumeToggle() {
    // 볼륨은 모든 디바이스 조절 가능하게
    //if(Utils.isMobileOrTableDevice()) {
    //  muteToggle();
    //} else {
      setShowVolume((prev) => !prev);
    //}
  }

  function muteToggle(): void {
    setMute(prev => {
      const _mute = !prev;
      onair.setMute(_mute);
      return _mute;
    })
  }

  function handleClick(event: MouseEvent | TouchEvent): void {
    if (
      volumeLayerRef.current && !volumeLayerRef.current.contains(event.target as Node) &&
      volumeWrapRef.current && !volumeWrapRef.current.contains(event.target as Node)
    ) {
      setShowVolume(false);
    }
  }

  function createSlider(sliderId: string, fillId: string, thumbId: string, valueId: string, setVolume: (value: number) => void, options: SliderOptions = {}): () => void {
    const container = document.getElementById(sliderId) as HTMLDivElement | null;
    const fill = document.getElementById(fillId) as HTMLDivElement | null;
    const thumb = document.getElementById(thumbId) as HTMLDivElement | null;
    const value = document.getElementById(valueId) as HTMLInputElement | null;

    if (!container || !fill || !thumb || !value) {
      return () => {}; // no-op
    }

    let dragging = false;
    const min = options.minValue ?? 0;
    const max = options.maxValue ?? 100;

    const getClientX = (e: MouseEvent | TouchEvent): number => {
      // TouchEvent가 정의되어 있고, 현재 이벤트가 TouchEvent의 인스턴스인 경우
      if (typeof TouchEvent !== 'undefined' && e instanceof TouchEvent) {
        // TouchEvent에는 clientX가 직접 없고, touches 배열 안에 Touch 객체가 있습니다.
        // 첫 번째 터치 포인트의 clientX를 사용합니다.
        return e.touches[0].clientX;
      } else {
        // MouseEvent이거나, TouchEvent가 정의되지 않은 환경인 경우 (데스크톱 사파리 등)
        // MouseEvent에는 clientX가 직접 있습니다.
        return (e as MouseEvent).clientX; // 여기서 MouseEvent로 타입 단언
      }
    };

    const updateByPercent = (percent: number) => {
      const clamped = Math.max(min, Math.min(max, percent));
      const width = container.getBoundingClientRect().width;
      const offsetX = (clamped / 100) * width;
      thumb.style.left = `${offsetX}px`;
      fill.style.width = `${offsetX}px`;
      value.value = clamped.toString();

      // Assuming onair.setVolume is defined elsewhere and handles the actual audio volume
      // Be mindful of iOS Safari's restrictions on HTMLAudioElement.volume
      if (typeof onair !== 'undefined' && typeof onair.setVolume === 'function') {
        onair.setVolume(clamped);
      }
      setVolume(clamped); // This setVolume is likely for the HTMLAudioElement
    };

    const updateByClientX = (clientX: number) => {
      const rect = container.getBoundingClientRect();
      let offsetX = clientX - rect.left;
      offsetX = Math.max(0, Math.min(rect.width, offsetX));
      const percent = Math.round((offsetX / rect.width) * 100);
      updateByPercent(percent);
    };

    const onDown = (e: MouseEvent | TouchEvent) => {
      dragging = true;
      e.preventDefault();
    };

    const onMove = (e: MouseEvent | TouchEvent) => {
      if (dragging) updateByClientX(getClientX(e));
    };

    const onUp = () => {
      dragging = false;
    };

    // 이벤트 등록
    thumb.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);

    thumb.addEventListener("touchstart", onDown, { passive: false });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);

    if (options.initialValue !== undefined) {
      updateByPercent(options.initialValue);
    }

    return () => {
      thumb.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);

      thumb.removeEventListener("touchstart", onDown);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// Onair
  const onOnairScheduleHandler = (_schedule: unknown) => {
    // print("onOnairScheduleHandler", _schedule);
  };

  const onOnairMediaHandler = (_media: unknown) => {
    const media: DaoMedia = _media as DaoMedia ?? null;
    setMedia(media);
  };

  const onOnairMediaStartHandler = (_media: unknown, _current: unknown) => { //media: unknown
    setMediaPosition(Math.floor(_current as number ?? 0));
  };

  const onOnairMediaEndHandler = () => { //media: unknown

  };

  const onOnairMediaErrorHandler = async () => { //media: unknown
    print("fail to load audio file", onair.media);
    // await onair.setMedia(null);
  };

  const onOnairDurationHandler = (_current: unknown, _total: unknown, _media: unknown) => {
    // 음원정보가 없는경우 추출
    if(!media) {
      const media: DaoMedia = _media as DaoMedia ?? null;
      if(media) setMedia(media);
    }
    setMediaPosition(Math.floor(_current as number ?? 0));
  };

  const getOnairName = (media: DaoMedia) => {
    if(media) {
      switch(media.type) {
        case "EM":
          return onair.getPriorityName(media.type);
        case "UD":
          return media.onair_name;
        default:
          return `${app.getClientName(false)}님 ${onair.getPriorityName(media.type)}`;
      }
    }
    return '';
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  return (
    <section className="player">
      <div className="image">
        <img src={(media && !_.isEmpty(media.album_image)) ? media.album_image : app.image_default}
             alt=""
             onClick={needResume}
             onError={(e) => {
               e.currentTarget.src = app.image_default;
             }} />
      </div>
      <div className="content">
        <div className="info-wrap">

          <div className="text-wrap">
            {media ?
              <>
                <div className="name">
                  {getOnairName(media)}

                  <svg width="16px" height="16px" strokeWidth="0.7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"
                       style={{marginLeft: '5px', cursor: 'pointer'}}
                       onClick={() => refresh()}>
                    <path d="M21.1679 8C19.6247 4.46819 16.1006 2 11.9999 2C6.81459 2 2.55104 5.94668 2.04932 11" stroke="#000000" strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M17 8H21.4C21.7314 8 22 7.73137 22 7.4V3" stroke="#000000" strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M2.88146 16C4.42458 19.5318 7.94874 22 12.0494 22C17.2347 22 21.4983 18.0533 22 13" stroke="#000000" strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M7.04932 16H2.64932C2.31795 16 2.04932 16.2686 2.04932 16.6V21" stroke="#000000" strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                </div>
                <div className="song" ref={songRef}>
                  <span className="title">
                    {media.url !== null && Utils.isLocalMedia(media.url) && <span className="is-local"></span>}
                    {media.title}{media.artist_name ? ` - ${media.artist_name}` : ''}
                  </span>
                </div>
              </> :
              <div className="song">재생 준비중입니다.</div>
            }
          </div>
          <div className="volume-wrap" ref={volumeWrapRef} onClick={() => volumeToggle()}>
            <a><img src={`/images/${mute ? 'icon-mute' : 'icon-vol'}.png`} alt="" /></a>
          </div>
        </div>
        <div className="audio-track" ref={trackRef}>
          <div className="time-info">
            {media ?
              <>
                <span className="current-time">{Utils.duration(mediaPosition, false)}</span>
                <span className="total-time">{Utils.duration(media.duration, false)}</span>
              </> :
              <></>
            }
          </div>
          <div className="progress-bar">
            <div className="progress-fill"
                 style={{ width: `${Math.max(Math.min((((mediaPosition ?? 0) / (media?.duration ?? 0)) * 100), 100), 0)}%` }}></div>
          </div>
        </div>
      </div>

      {/** Volume Layer */}
      <div className="volume-layer" ref={volumeLayerRef} style={{ display: showVolume ? 'block' : 'none' }}>
        <div className="volume-container">
          <div className="controller-wrap">
            <input type="hidden" value={volume} id="slider-value-1" />
            <div id="slider-container-1" className="slider-container">
              <div className="tick" style={{ left: '25%' }}></div>
              <div className="tick" style={{ left: '50%' }}></div>
              <div className="tick" style={{ left: '75%' }}></div>

              <div id="slider-fill-1" className="slider-fill"></div>
              <div id="slider-thumb-1" className="slider-thumb"></div>
            </div>
            <div className="volume-icon" onClick={muteToggle}>
              <img src={`/images/${mute ? 'icon-mute' : 'icon-vol'}.png`} alt=""/>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Player;